/**
 * **游戏规则设置的注册表** —— 谁拥有那条规则，谁负责登记它。
 *
 * 以前房间页是一串手写的 stepper/toggle，加一条规则就要在**界面文件**里再
 * 写一遍"它叫什么、怎么读、怎么写"。那等于把同一件事实写在两个地方，迟早
 * 对不上（"玩家数"那条就是这么过时的）。
 *
 * 现在改成**注册表**：
 *
 * - 每条设置是一个 HouseRule 描述（叫什么、什么类型、怎么读、怎么写成 patch）；
 * - **拥有这条规则的功能模块**在导入时调用 registerHouseRule 登记它；
 * - 房间页只做一件事：houseRules.all() 然后渲染。
 *
 * 于是"有哪些设置、什么类型、怎么生效"只有**一份事实**，就在各功能模块里。
 *
 * ## ⚠️ 为什么住在 core/ 而不是 ui/
 *
 * 登记发生在**使用这个设置项数据的功能实现**里（开局在 core/setup.ts、
 * 连击/出局打断在 core/reducer.ts）。如果注册表住在 ui/，core/ 的功能模块
 * 就得反向依赖界面层。所以它跟着 ConfigPatch 一起下沉到 core/ —— 界面只
 * 负责 all() 然后画出来。
 *
 * ⚠️ id 必须唯一 —— 两个模块登记同一个 id 是**错误**（那正是"两份事实"）。
 */
import type { ConfigPatch, RoomConfig } from './config';
import { t, type TextKey } from '../i18n';

/** 这个设置项在界面上长什么样 */
export type RuleControl =
  /** 布尔开关 */
  | { readonly kind: 'toggle' }
  /** 纯数字步进 */
  | { readonly kind: 'number'; readonly min: number; readonly max: number; readonly step?: number }
  /**
   * **自动 / 手调**二选一。值是 number | null，null = 自动（用官方值）。
   * 界面上是一个"自动"开关 + （关掉时）一个步进器。
   */
  | {
      readonly kind: 'auto-number';
      readonly min: number;
      readonly max: number;
      readonly step?: number;
      readonly autoValue: (config: RoomConfig) => number;
      readonly autoNote: (config: RoomConfig) => string;
      readonly manualFallback: (config: RoomConfig) => number;
    }
  /**
   * **开关 + 数值**：开关表示"不限"（值 = offValue），关着时显示滑块。
   */
  | {
      readonly kind: 'toggle-number';
      readonly min: number;
      readonly max: number;
      readonly step: number;
      readonly offValue: number;
      readonly onValue: number;
      /** ⚠️ 是 i18n 键，不是文案 —— 渲染时再 t()，否则会冻在导入时的语言 */
      readonly toggleLabel: TextKey;
      readonly format?: (value: number) => string;
    };

export interface HouseRule {
  /** 全局唯一。惯例：字段名 或 分组.字段名 */
  readonly id: string;
  /** 分组标题（i18n 键）。相邻同名的行归到一个 section 下 */
  readonly section: TextKey;
  readonly label: TextKey;
  readonly control: RuleControl;
  /** 读当前值（开关给 boolean，数字给 number，auto-number 给 number|null） */
  readonly value: (config: RoomConfig) => number | boolean | null;
  /** 写成一次 config patch */
  readonly patch: (value: number | boolean | null) => ConfigPatch;
  /** 注脚（i18n 键）。渲染时再 t()，理由同 `toggleLabel` */
  readonly note?: TextKey;
  /** 额外的禁用条件（"只有创建者能改"由渲染方统一加） */
  readonly enabled?: (config: RoomConfig) => boolean;
}

class HouseRuleRegistry {
  readonly #rules: HouseRule[] = [];
  readonly #ids = new Set<string>();

  register(rule: HouseRule): void {
    if (this.#ids.has(rule.id)) {
      throw new Error(t('core.houseRules.duplicate', { id: rule.id }));
    }
    this.#ids.add(rule.id);
    this.#rules.push(rule);
  }

  /** 按登记顺序返回全部规则 */
  all(): readonly HouseRule[] {
    return [...this.#rules];
  }

  byId(id: string): HouseRule | undefined {
    return this.#rules.find((rule) => rule.id === id);
  }

  /** 清空。只给测试用 —— 正常程序里注册发生在导入期，只跑一次 */
  clearForTest(): void {
    this.#rules.length = 0;
    this.#ids.clear();
  }
}

/** 全游戏规则的唯一注册表 */
export const houseRules = new HouseRuleRegistry();

/** 功能模块在导入时调它登记自己的设置项 */
export function registerHouseRule(rule: HouseRule): void {
  houseRules.register(rule);
}
